import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Globe, Moon, Sun, Smartphone, Trash2, ChevronRight, CheckCircle2, X, Monitor, Bell, Phone, CalendarCheck, BarChart3, AlertTriangle, Megaphone } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
interface SettingsScreenProps {
  onBack: () => void;
}
const APP_LANGUAGES = ['English', 'العربية'];
export function SettingsScreen({
  onBack
}: SettingsScreenProps) {
  const {
    isLight,
    themeMode,
    setThemeMode
  } = useTheme();
  const [appLanguage, setAppLanguage] = useState('English');
  const [showLanguagePicker, setShowLanguagePicker] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Change phone number flow
  const [phoneFlow, setPhoneFlow] = useState<'idle' | 'enter' | 'otp' | 'done'>('idle');
  const [newPhone, setNewPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [resendTimer, setResendTimer] = useState(60);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Notification preferences
  const [notifications, setNotifications] = useState<Record<string, boolean>>({
    callHandled: true,
    meetingBooked: true,
    weeklySummary: true,
    overageAlert: true,
    tips: false
  });
  const toggleNotification = (id: string) => {
    setNotifications(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  const notificationSettings = [{
    id: 'callHandled',
    icon: Phone,
    label: 'Call Handled',
    desc: 'When AI answers a call for you'
  }, {
    id: 'meetingBooked',
    icon: CalendarCheck,
    label: 'Meeting Booked',
    desc: 'When a caller books a meeting'
  }, {
    id: 'weeklySummary',
    icon: BarChart3,
    label: 'Weekly Summary',
    desc: 'Usage and activity recap'
  }, {
    id: 'overageAlert',
    icon: AlertTriangle,
    label: 'Overage Alerts',
    desc: 'When you exceed your plan minutes'
  }, {
    id: 'tips',
    icon: Megaphone,
    label: 'Tips & Updates',
    desc: 'Product news and helpful tips'
  }];
  // Start resend timer when entering OTP step
  useEffect(() => {
    if (phoneFlow === 'otp') {
      setResendTimer(60);
      timerRef.current = setInterval(() => {
        setResendTimer(prev => {
          if (prev <= 1) {
            if (timerRef.current) clearInterval(timerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [phoneFlow]);
  const themeOptions: Array<{
    mode: 'auto' | 'light' | 'dark';
    label: string;
    icon: React.ElementType;
    desc: string;
  }> = [{
    mode: 'auto',
    label: 'Auto',
    icon: Monitor,
    desc: 'Follow phone settings'
  }, {
    mode: 'light',
    label: 'Light',
    icon: Sun,
    desc: 'Always light'
  }, {
    mode: 'dark',
    label: 'Dark',
    icon: Moon,
    desc: 'Always dark'
  }];
  return <div className="h-full flex flex-col relative" data-id="element-3674" data-mp="37tpmwc5p6cgr">
      <div className={`absolute top-20 right-0 w-36 h-36 rounded-full blur-3xl pointer-events-none ${isLight ? 'bg-vexu/15' : 'bg-vexu/10'}`} data-id="element-3675" data-mp="32veem5v1wa0n" />

      {/* Header */}
      <div className={`p-4 border-b flex items-center gap-3 sticky top-0 backdrop-blur-2xl z-10 transition-colors duration-500 ${isLight ? 'bg-white/80 border-slate-200/50' : 'bg-dark/80 border-white/5'}`} data-id="element-3676" data-mp="2tw9gh0neiip0">
        <button onClick={onBack} className={`p-2 -ml-2 rounded-full border transition-colors ${isLight ? 'text-slate-500 hover:text-slate-900 bg-white/60 backdrop-blur-md border-slate-200' : 'text-zinc-400 hover:text-white bg-white/5 backdrop-blur-md border-white/10'}`} data-id="element-3677" data-mp="2k6zjp7hm4buk">
          <ArrowLeft className="w-5 h-5" data-id="element-3678" data-mp="267hu4pwhsuk5" />
        </button>
        <span className={`font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3679" data-mp="a3ae58zj8nxd">
          Settings
        </span>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6 pb-24 relative z-10" data-id="element-3680" data-mp="e44222cz62q9">
        {/* Appearance */}
        <section className="space-y-3" data-id="element-3681" data-mp="cyxqmynwm9l">
          <h3 className={`text-[11px] font-bold uppercase tracking-wider ml-1 ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-3682" data-mp="5c254e2474kq">
            Appearance
          </h3>
          <div className={`backdrop-blur-xl border rounded-2xl p-2 shadow-sm transition-colors ${isLight ? 'bg-white/80 border-slate-200/60' : 'bg-dark-card/60 border-white/10'}`} data-id="element-3683" data-mp="3rjiiouy7pslm">
            <div className="grid grid-cols-3 gap-1.5" data-id="element-3684" data-mp="3iikbk3sh94yt">
              {themeOptions.map(opt => {
              const Icon = opt.icon;
              const isSelected = themeMode === opt.mode;
              return <button key={opt.mode} onClick={() => setThemeMode(opt.mode)} className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl transition-all ${isSelected ? isLight ? 'bg-vexu/10 ring-1 ring-vexu/30' : 'bg-vexu/10 ring-1 ring-vexu/30' : isLight ? 'hover:bg-slate-50' : 'hover:bg-white/5'}`} data-id="element-3685" data-mp="1grqrp4bgi1l0">
                    <Icon className={`w-5 h-5 ${isSelected ? isLight ? 'text-vexu-600' : 'text-vexu' : isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-3686" data-mp="1uwr16raaw467" />
                    <span className={`text-xs font-semibold ${isSelected ? isLight ? 'text-vexu-700' : 'text-vexu' : isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-3687" data-mp="24nlp00373btr">
                      {opt.label}
                    </span>
                    <span className={`text-[9px] ${isSelected ? isLight ? 'text-vexu-600' : 'text-vexu/80' : isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-3688" data-mp="o8kgcrat3bry">
                      {opt.desc}
                    </span>
                  </button>;
            })}
            </div>
          </div>
        </section>

        {/* Language */}
        <section className="space-y-3" data-id="element-3689" data-mp="2t9wkv5g3i24m">
          <h3 className={`text-[11px] font-bold uppercase tracking-wider ml-1 ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-3690" data-mp="2t8v88wjfl7ks">
            Language
          </h3>
          <div className={`backdrop-blur-xl border rounded-2xl overflow-hidden shadow-sm transition-colors ${isLight ? 'bg-white/80 border-slate-200/60' : 'bg-dark-card/60 border-white/10'}`} data-id="element-3691" data-mp="32bcw1feqi89w">
            <button onClick={() => setShowLanguagePicker(true)} className={`w-full p-4 flex items-center justify-between transition-colors text-left ${isLight ? 'hover:bg-slate-50' : 'hover:bg-white/5'}`} data-id="element-3692" data-mp="3c1fq7ixlds9t">
              <div className="flex items-center gap-3" data-id="element-3693" data-mp="3lwr644kql5j5">
                <Globe className={`w-5 h-5 ${isLight ? 'text-slate-400' : 'text-zinc-400'}`} data-id="element-3694" data-mp="3q0yv82v3wnhw" />
                <div data-id="element-3695" data-mp="1eapmp9m6ymtb">
                  <div className={`text-sm font-medium ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3696" data-mp="1abgsy4r5ioxr">
                    App Language
                  </div>
                  <div className={`text-xs ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-3697" data-mp="10das7qh2zouh">
                    {appLanguage}
                  </div>
                </div>
              </div>
              <ChevronRight className={`w-4 h-4 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-3698" data-mp="1t1ge4gjvfjvw" />
            </button>
          </div>
        </section>

        {/* Notifications */}
        <section className="space-y-3" data-id="element-3699" data-mp="1jb3vitvma0se">
          <h3 className={`text-[11px] font-bold uppercase tracking-wider ml-1 ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-3700" data-mp="1ja2iwkyyd68k">
            Notifications
          </h3>
          <div className={`backdrop-blur-xl border rounded-2xl overflow-hidden shadow-sm transition-colors ${isLight ? 'bg-white/80 border-slate-200/60' : 'bg-dark-card/60 border-white/10'}`} data-id="element-3701" data-mp="2byhlu94t7y3j">
            {notificationSettings.map((setting, i) => {
            const Icon = setting.icon;
            return <div key={setting.id} className={`flex items-center justify-between p-4 ${i < notificationSettings.length - 1 ? isLight ? 'border-b border-slate-100' : 'border-b border-white/5' : ''}`} data-id="element-3702" data-mp="1fdem3na5zgbt" data-mpi={i}>
                  <div className="flex items-center gap-3 flex-1 min-w-0" data-id="element-3703" data-mp="280hhw7nghcyd">
                    <Icon className={`w-5 h-5 flex-shrink-0 ${isLight ? 'text-slate-400' : 'text-zinc-400'}`} data-id="element-3704" data-mp="1u2atdi4ecpbd" />
                    <div className="min-w-0" data-id="element-3705" data-mp="in4cef3lxivo">
                      <div className={`text-sm font-medium ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3706" data-mp="im2r7hsvng08">
                        {setting.label}
                      </div>
                      <div className={`text-xs truncate ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-3707" data-mp="f7kp94oazpoh">
                        {setting.desc}
                      </div>
                    </div>
                  </div>
                  <button onClick={() => toggleNotification(setting.id)} className={`relative w-11 h-6 rounded-full flex-shrink-0 ml-3 transition-colors duration-200 ${notifications[setting.id] ? 'bg-vexu' : isLight ? 'bg-slate-200' : 'bg-white/10'}`} data-id="element-3708" data-mp="k5cfzvd7p5r3">
                    <motion.div layout transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 30
                }} className={`absolute top-0.5 w-5 h-5 rounded-full shadow-sm ${notifications[setting.id] ? 'left-[22px] bg-white' : 'left-0.5 ' + (isLight ? 'bg-white' : 'bg-zinc-400')}`} data-id="element-3709" data-mp="66csws5ckyx1" />
                  </button>
                </div>;
          })}
          </div>
        </section>

        {/* Account */}
        <section className="space-y-3" data-id="element-3710" data-mp="1xir5cx9vzu3n">
          <h3 className={`text-[11px] font-bold uppercase tracking-wider ml-1 ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-3711" data-mp="1sl95dwdukp7s">
            Account
          </h3>
          <div className={`backdrop-blur-xl border rounded-2xl overflow-hidden shadow-sm transition-colors ${isLight ? 'bg-white/80 border-slate-200/60' : 'bg-dark-card/60 border-white/10'}`} data-id="element-3712" data-mp="1olgvngcpwd0u">
            <button onClick={() => setPhoneFlow('enter')} className={`w-full p-4 flex items-center justify-between transition-colors text-left ${isLight ? 'hover:bg-slate-50 border-b border-slate-100' : 'hover:bg-white/5 border-b border-white/5'}`} data-id="element-3713" data-mp="19uzuly3zvp4h">
              <div className="flex items-center gap-3" data-id="element-3714" data-mp="3chezmgaz6ljx">
                <Smartphone className={`w-5 h-5 ${isLight ? 'text-slate-400' : 'text-zinc-400'}`} data-id="element-3715" data-mp="3cgvfqu1rln39" />
                <div data-id="element-3716" data-mp="3gkutx5zko9tg">
                  <div className={`text-sm font-medium ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3717" data-mp="3lhl81nrtgvc8">
                    Change Phone Number
                  </div>
                  <div className={`text-xs ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-3718" data-mp="2stxpsfe0qgsr">
                    Verify new number with OTP
                  </div>
                </div>
              </div>
              <ChevronRight className={`w-4 h-4 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-3719" data-mp="32qaj3c5nismz" />
            </button>
            <button className={`w-full p-4 flex items-center justify-between transition-colors text-left ${isLight ? 'hover:bg-slate-50' : 'hover:bg-white/5'}`} data-id="element-3720" data-mp="2a3g1cn07y7d7">
              <div className="flex items-center gap-3" data-id="element-3721" data-mp="27138ew25hdzu">
                <Smartphone className={`w-5 h-5 ${isLight ? 'text-slate-400' : 'text-zinc-400'}`} data-id="element-3722" data-mp="26zicp343np0e" />
                <div data-id="element-3723" data-mp="2gr4szdvaoea0">
                  <div className={`text-sm font-medium ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3724" data-mp="2greb6fa3lh55">
                    Linked Devices
                  </div>
                  <div className={`text-xs ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-3725" data-mp="1o1p9dzpkdd2h">
                    1 device connected
                  </div>
                </div>
              </div>
              <ChevronRight className={`w-4 h-4 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-3726" data-mp="1y1y93nirlgmm" />
            </button>
          </div>
        </section>

        {/* Danger Zone */}
        <section className="space-y-3" data-id="element-3727" data-mp="39hwosa8g70wt">
          <h3 className={`text-[11px] font-bold uppercase tracking-wider ml-1 ${isLight ? 'text-red-400' : 'text-red-500/70'}`} data-id="element-3728" data-mp="34h8o0wuf35pe">
            Danger Zone
          </h3>
          <button onClick={() => setShowDeleteConfirm(true)} className={`w-full p-4 flex items-center gap-3 rounded-2xl border transition-colors ${isLight ? 'bg-white/80 border-red-100 hover:bg-red-50' : 'bg-dark-card/60 border-red-500/10 hover:bg-red-500/5'}`} data-id="element-3729" data-mp="v7su6mc4y9ju">
            <Trash2 className={`w-5 h-5 ${isLight ? 'text-red-500' : 'text-red-400'}`} data-id="element-3730" data-mp="19x74pxrg6x0t" />
            <div className="text-left" data-id="element-3731" data-mp="1dys9swxqq70g">
              <div className={`text-sm font-medium ${isLight ? 'text-red-600' : 'text-red-400'}`} data-id="element-3732" data-mp="1dxqx6o12tcgm">
                Delete Account
              </div>
              <div className={`text-xs ${isLight ? 'text-red-400' : 'text-red-500/60'}`} data-id="element-3733" data-mp="154xeegmf7uy0">
                Permanently remove your account and data
              </div>
            </div>
          </button>
        </section>

        {/* App Info */}
        <div className={`text-center pt-2 ${isLight ? 'text-slate-400' : 'text-zinc-600'}`} data-id="element-3734" data-mp="90ypet9pmw3r">
          <p className="text-[11px] font-medium" data-id="element-3735" data-mp="40kdv18wh40z">Vexu.ai v1.0.0</p>
          <p className="text-[10px] mt-0.5" data-id="element-3736" data-mp="3w54rrc9f0a1l">Made with ♥ in Dubai</p>
        </div>
      </div>

      {/* Language Picker Modal */}
      <AnimatePresence data-id="element-3737" data-mp="3r7d10lkiatyz">
        {showLanguagePicker && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 z-50 flex items-end justify-center p-4" data-id="element-3738" data-mp="rr6lwsiwmoaz">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowLanguagePicker(false)} data-id="element-3739" data-mp="e01jjio5jhwy" />
            <motion.div initial={{
          y: 200,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 200,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 400,
          damping: 30
        }} className={`relative w-full max-w-sm rounded-3xl overflow-hidden max-h-[60vh] ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border border-white/10 shadow-2xl'}`} data-id="element-3740" data-mp="1bgi6jexf54si">
              <div className="p-5 pb-3 flex justify-between items-center" data-id="element-3741" data-mp="21xkexzdfgt9o">
                <h3 className={`text-base font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3742" data-mp="21vhhiu9y3xux">
                  App Language
                </h3>
                <button onClick={() => setShowLanguagePicker(false)} className={`p-1.5 rounded-full ${isLight ? 'text-slate-400 hover:bg-slate-100' : 'text-zinc-500 hover:bg-white/10'}`} data-id="element-3743" data-mp="2bmkhfsr526sm">
                  <X className="w-5 h-5" data-id="element-3744" data-mp="klj46ir2mx1n" />
                </button>
              </div>
              <div className="overflow-y-auto max-h-[40vh] pb-4" data-id="element-3745" data-mp="gk7ekd876btg">
                {APP_LANGUAGES.map(lang => <button key={lang} onClick={() => {
              setAppLanguage(lang);
              setShowLanguagePicker(false);
            }} className={`w-full flex items-center justify-between px-5 py-3 transition-colors ${lang === appLanguage ? isLight ? 'bg-vexu/5' : 'bg-vexu/[0.06]' : isLight ? 'hover:bg-slate-50' : 'hover:bg-white/[0.03]'}`} data-id="element-3746" data-mp="uc5f3znueze0">
                    <span className={`text-sm font-medium ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3747" data-mp="1d8dmpa4bvmch">
                      {lang}
                    </span>
                    {lang === appLanguage && <CheckCircle2 className="w-4 h-4 text-vexu" data-id="element-3748" data-mp="3a3jftpr7xoio" />}
                  </button>)}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Change Phone Number Flow */}
      <AnimatePresence data-id="element-3749" data-mp="1gsnk4ysoqo0l">
        {phoneFlow !== 'idle' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 z-50 flex items-end justify-center p-4" data-id="element-3750" data-mp="1gs5fbdryj1h2">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => {
          setPhoneFlow('idle');
          setNewPhone('');
          setOtpCode('');
        }} data-id="element-3751" data-mp="1bulyq77secly" />
            <motion.div initial={{
          y: 200,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 200,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 400,
          damping: 30
        }} className={`relative w-full max-w-sm rounded-3xl p-6 space-y-4 ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border border-white/10 shadow-2xl'}`} data-id="element-3752" data-mp="2s7avnkl0gyj5">
              {phoneFlow === 'enter' && <>
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isLight ? 'bg-vexu/10' : 'bg-vexu/10'}`} data-id="element-3753" data-mp="wahqdz1j034g">
                    <Smartphone className={`w-6 h-6 ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-3754" data-mp="rcyf9804k6gq" />
                  </div>
                  <div data-id="element-3755" data-mp="2lnv2wbjskxgn">
                    <h3 className={`text-lg font-bold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3756" data-mp="2ln22dsbff46y">
                      New Phone Number
                    </h3>
                    <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-3757" data-mp="2p598mtmwa6or">
                      Enter your new phone number. We'll send an OTP to verify
                      it.
                    </p>
                  </div>
                  <input type="tel" value={newPhone} onChange={e => setNewPhone(e.target.value)} placeholder="+1 (555) 000-0000" className={`w-full px-4 py-3 rounded-xl text-sm font-medium outline-none focus:outline-none focus:ring-2 focus:ring-vexu/30 transition-colors ${isLight ? 'bg-slate-50 border border-slate-200 text-slate-900 placeholder:text-slate-400' : 'bg-white/5 border border-white/10 text-white placeholder:text-zinc-500'}`} data-id="element-3758" data-mp="330l26xpbdr43" />
                  <div className="flex gap-2" data-id="element-3759" data-mp="3vlurq3vwn5j6">
                    <button onClick={() => {
                setPhoneFlow('idle');
                setNewPhone('');
              }} className={`flex-1 py-3 rounded-xl font-medium text-sm ${isLight ? 'bg-slate-100 text-slate-700 hover:bg-slate-200' : 'bg-white/5 text-zinc-300 hover:bg-white/10'}`} data-id="element-3760" data-mp="3voz0zfo88rgp">
                      Cancel
                    </button>
                    <button onClick={() => newPhone.trim() && setPhoneFlow('otp')} className={`flex-1 py-3 rounded-xl font-semibold text-sm transition-colors ${newPhone.trim() ? 'bg-vexu text-white hover:bg-vexu-600' : isLight ? 'bg-slate-100 text-slate-400' : 'bg-white/5 text-zinc-600'}`} data-id="element-3761" data-mp="ser4c3ss0rkb">
                      Send OTP
                    </button>
                  </div>
                </>}

              {phoneFlow === 'otp' && <>
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isLight ? 'bg-vexu/10' : 'bg-vexu/10'}`} data-id="element-3762" data-mp="2huqla72mzlai">
                    <Smartphone className={`w-6 h-6 ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-3763" data-mp="2508uoty0viv" />
                  </div>
                  <div data-id="element-3764" data-mp="3ojtaw2ghq5rv">
                    <h3 className={`text-lg font-bold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3765" data-mp="3oi8dm52sk8qb">
                      Verify OTP
                    </h3>
                    <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-3766" data-mp="3ekc41hpi5lub">
                      Enter the 6-digit code sent to{' '}
                      <span className="font-semibold" data-id="element-3767" data-mp="35lqqk0yw0x0e">{newPhone}</span>
                    </p>
                  </div>
                  <input type="text" value={otpCode} onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" maxLength={6} className={`w-full px-4 py-3 rounded-xl text-center text-2xl font-bold tracking-[0.3em] outline-none focus:outline-none focus:ring-2 focus:ring-vexu/30 transition-colors ${isLight ? 'bg-slate-50 border border-slate-200 text-slate-900 placeholder:text-slate-300' : 'bg-white/5 border border-white/10 text-white placeholder:text-zinc-600'}`} data-id="element-3768" data-mp="18rdv7wszaa07" />
                  <button disabled={resendTimer > 0} onClick={() => {
              if (resendTimer === 0) {
                setResendTimer(60);
                timerRef.current = setInterval(() => {
                  setResendTimer(prev => {
                    if (prev <= 1) {
                      if (timerRef.current) clearInterval(timerRef.current);
                      return 0;
                    }
                    return prev - 1;
                  });
                }, 1000);
              }
            }} className={`text-xs font-medium text-center w-full ${resendTimer > 0 ? isLight ? 'text-slate-400' : 'text-zinc-500' : isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-3769" data-mp="2amqaop3ibz9c">
                    {resendTimer > 0 ? `Resend code in ${resendTimer}s` : 'Resend code'}
                  </button>
                  <div className="flex gap-2" data-id="element-3770" data-mp="8q9ftusx11bb">
                    <button onClick={() => setPhoneFlow('enter')} className={`flex-1 py-3 rounded-xl font-medium text-sm ${isLight ? 'bg-slate-100 text-slate-700 hover:bg-slate-200' : 'bg-white/5 text-zinc-300 hover:bg-white/10'}`} data-id="element-3771" data-mp="8r0y63xi52jn">
                      Back
                    </button>
                    <button onClick={() => otpCode.length === 6 && setPhoneFlow('done')} className={`flex-1 py-3 rounded-xl font-semibold text-sm transition-colors ${otpCode.length === 6 ? 'bg-vexu text-white hover:bg-vexu-600' : isLight ? 'bg-slate-100 text-slate-400' : 'bg-white/5 text-zinc-600'}`} data-id="element-3772" data-mp="3l2yqwk2vhl9e">
                      Verify
                    </button>
                  </div>
                </>}

              {phoneFlow === 'done' && <>
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-vexu/10" data-id="element-3773" data-mp="hl6wkz19kp81">
                    <CheckCircle2 className="w-6 h-6 text-vexu" data-id="element-3774" data-mp="mlcqklv0anta" />
                  </div>
                  <div data-id="element-3775" data-mp="2jdcxbu0nk27g">
                    <h3 className={`text-lg font-bold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3776" data-mp="2je4uvadt8a0d">
                      Number Updated!
                    </h3>
                    <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-3777" data-mp="15hv3r4n9d535">
                      Your phone number has been changed to{' '}
                      <span className="font-semibold" data-id="element-3778" data-mp="wp9mxu36t8gy">{newPhone}</span>
                    </p>
                  </div>
                  <button onClick={() => {
              setPhoneFlow('idle');
              setNewPhone('');
              setOtpCode('');
            }} className="w-full py-3 rounded-xl font-semibold text-sm bg-vexu text-white hover:bg-vexu-600 transition-colors" data-id="element-3779" data-mp="rrht2uizfca4">
                    Done
                  </button>
                </>}
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Delete Account Confirmation */}
      <AnimatePresence data-id="element-3780" data-mp="3v5c7s3x81ywc">
        {showDeleteConfirm && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 z-50 flex items-end justify-center p-4" data-id="element-3781" data-mp="i58jih81pk4">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowDeleteConfirm(false)} data-id="element-3782" data-mp="3slxoic0o2lra" />
            <motion.div initial={{
          y: 100,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 100,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 400,
          damping: 30
        }} className={`relative w-full max-w-sm rounded-3xl p-6 space-y-4 ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border border-white/10 shadow-2xl'}`} data-id="element-3783" data-mp="3dvi0kpgv8zpf">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isLight ? 'bg-red-50' : 'bg-red-500/10'}`} data-id="element-3784" data-mp="2gdkxo3otggg8">
                <Trash2 className={`w-6 h-6 ${isLight ? 'text-red-500' : 'text-red-400'}`} data-id="element-3785" data-mp="2qafqq72blon4" />
              </div>
              <div data-id="element-3786" data-mp="2zacmesn4nnro">
                <h3 className={`text-lg font-bold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3787" data-mp="2z9ugf4anxv6k">
                  Delete Account?
                </h3>
                <p className={`text-sm leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-3788" data-mp="38zw620inzlnl">
                  This will permanently delete your account, call history, and
                  all settings. This action cannot be undone.
                </p>
              </div>
              <div className="flex gap-2" data-id="element-3789" data-mp="3e01t13dzojzc">
                <button onClick={() => setShowDeleteConfirm(false)} className={`flex-1 py-3 rounded-xl font-medium text-sm ${isLight ? 'bg-slate-100 text-slate-700 hover:bg-slate-200' : 'bg-white/5 text-zinc-300 hover:bg-white/10'}`} data-id="element-3790" data-mp="3dx5tjamhtn8s">
                  Cancel
                </button>
                <button onClick={() => setShowDeleteConfirm(false)} className="flex-1 py-3 rounded-xl font-semibold text-sm bg-red-500 text-white hover:bg-red-600 transition-colors" data-id="element-3791" data-mp="1293l9ilzxah6">
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}